/**
 * The Liyu logo as a see-through loading screen, for the two slow waits: the
 * check for whether this browser is already signed in, and the time between
 * pressing Sign in and the app drawing its first screen.
 *
 * The page behind stays visible through a blur; the logo builds itself on a
 * dark glass card. If the app is ready first, the card fades out at once
 * rather than making anybody watch the rest. If the app is slower than the
 * animation, the finished logo stays with a light running across it until it
 * is ready. Pressing Sign in moves from the form to the app, so the start time
 * is kept in sessionStorage and the app page carries the animation on from
 * where the form left it instead of starting over.
 *
 * The motto is part of the logo artwork, like MANAGEMENT, and is drawn in
 * English in both languages.
 */

const MOTTO = ["Community living,", "made simple."] as const;

/** Same geometry as logo-mark.svg (T2 Setbacks), with the key in the roof. */
const TOWERS = [
  { clip: "M44 147V66H54V56H95V147Z", top: 56, start: 0.15 },
  { clip: "M99 147V48H106V38H136V48H143V147Z", top: 38, start: 0.25 },
  { clip: "M148 147V56H188V66H198V147Z", top: 56, start: 0.15 },
];

function floors(tower: (typeof TOWERS)[number], cls = "") {
  const ys: number[] = [];
  for (let y = tower.top; y < 146.5; y += 6.4) ys.push(+y.toFixed(1));
  return ys
    .map((y, i) => {
      const height = Math.min(4.2, 147 - y).toFixed(1);
      const delay = (tower.start + (ys.length - 1 - i) * 0.026).toFixed(3);
      const attrs = cls ? ` class="${cls}" style="--d:${delay}s"` : "";
      return `<rect${attrs} x="30" y="${y}" width="180" height="${height}"/>`;
    })
    .join("");
}

const LIYU_X = 124.5; // letter-spacing adds space after the last letter, so centre plus half of it
const LIYU_STYLE = "font:600 24px Montserrat,sans-serif;letter-spacing:9px";
const AM_STYLE = "font:600 27px 'Noto Sans Ethiopic',sans-serif";
const GRADIENT = (id: string, stops: string[], x1: number, x2: number) =>
  `<linearGradient id="${id}" gradientUnits="userSpaceOnUse" x1="${x1}" y1="0" x2="${x2}" y2="0">${stops
    .map((c, i) => `<stop offset="${i / (stops.length - 1)}" stop-color="${c}"/>`)
    .join("")}</linearGradient>`;
const SILVER = ["#8C97A3", "#F5F7F9", "#C9D1D9", "#F5F7F9", "#8C97A3"];
const BLUE = ["#2F7BC2", "#9FD0F7", "#5DA6E3", "#9FD0F7", "#2F7BC2"];

const [mottoLead, mottoTail] = MOTTO;
const mottoWords = [
  ...mottoLead.split(" ").map((w) => [w, false] as const),
  ...mottoTail.split(" ").map((w) => [w, true] as const),
];

const LOGO_SVG = `<svg class="liyu-logo" viewBox="12 8 216 226" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
<defs>
${GRADIENT("ll-metal", SILVER, 40, 200)}
${GRADIENT("ll-accent", BLUE, 40, 200)}
${GRADIENT("ll-metal-text", ["#9AA5B1", "#F5F7F9", "#C9D1D9", "#F5F7F9", "#9AA5B1"], 80, 160)}
${GRADIENT("ll-accent-text", BLUE, 36, 208)}
<linearGradient id="ll-sheen" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="#5DA6E3" stop-opacity="0"/><stop offset=".3" stop-color="#5DA6E3" stop-opacity=".45"/><stop offset=".5" stop-color="#fff"/><stop offset=".7" stop-color="#5DA6E3" stop-opacity=".45"/><stop offset="1" stop-color="#5DA6E3" stop-opacity="0"/></linearGradient>
<linearGradient id="ll-spark" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="#fff"/><stop offset=".06" stop-color="#9FD0F7" stop-opacity="0"/><stop offset=".94" stop-color="#9FD0F7" stop-opacity="0"/><stop offset="1" stop-color="#fff"/></linearGradient>
<radialGradient id="ll-glow"><stop offset="0" stop-color="#5DA6E3" stop-opacity=".55"/><stop offset="1" stop-color="#5DA6E3" stop-opacity="0"/></radialGradient>
<radialGradient id="ll-tip"><stop offset="0" stop-color="#fff"/><stop offset=".35" stop-color="#9FD0F7" stop-opacity=".8"/><stop offset="1" stop-color="#5DA6E3" stop-opacity="0"/></radialGradient>
${TOWERS.map((t, i) => `<clipPath id="ll-t${i}"><path d="${t.clip}"/></clipPath>`).join("")}
<mask id="ll-roofcut"><rect width="240" height="240" fill="#fff"/><path d="M84 147L121 109L158 147Z" fill="#000"/></mask>
<mask id="ll-floors"><g mask="url(#ll-roofcut)" fill="#fff">${TOWERS.map((t, i) => `<g clip-path="url(#ll-t${i})">${floors(t)}</g>`).join("")}</g></mask>
<mask id="ll-liyu"><text x="${LIYU_X}" y="188" text-anchor="middle" fill="#fff" style="${LIYU_STYLE}">LIYU</text></mask>
<clipPath id="ll-roof"><rect class="ll-roofclip" x="80" y="105" width="82" height="45"/></clipPath>
<filter id="ll-goo" x="-30%" y="-80%" width="160%" height="260%" color-interpolation-filters="sRGB"><feColorMatrix class="ll-goo-matrix" type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 20 -6"/></filter>
<filter id="ll-blur-am" x="-60%" y="-120%" width="220%" height="340%"><feGaussianBlur class="ll-blur-am" stdDeviation="0"/></filter>
<filter id="ll-blur-en" x="-60%" y="-120%" width="220%" height="340%"><feGaussianBlur class="ll-blur-en" stdDeviation="0"/></filter>
<filter id="ll-blur-pool" x="-40%" y="-200%" width="180%" height="500%"><feGaussianBlur stdDeviation="1.6"/></filter>
</defs>
<g class="ll-all">
<ellipse class="ll-glow" cx="121" cy="100" rx="95" ry="70" fill="url(#ll-glow)"/>
<g mask="url(#ll-roofcut)">
${TOWERS.map((t, i) => `<g clip-path="url(#ll-t${i})" fill="url(#ll-metal)">${floors(t, "ll-floor")}</g>`).join("")}
${TOWERS.map((t, i) => `<g clip-path="url(#ll-t${i})" fill="#DCEFFF">${floors(t, "ll-lit")}</g>`).join("")}
<rect class="ll-spire" x="120" y="22" width="2" height="16" fill="url(#ll-accent)"/>
</g>
<circle class="ll-tip" cx="121" cy="22" r="5" fill="url(#ll-tip)"/>
<g mask="url(#ll-floors)"><g transform="skewX(-14)"><rect class="ll-sheen" x="20" y="20" width="44" height="140" fill="url(#ll-sheen)"/></g></g>
<g clip-path="url(#ll-roof)"><path d="M86 146L121 111L156 146" fill="none" stroke="url(#ll-accent)" stroke-width="4.2"/></g>
<path class="ll-inner" d="M98 146L121 123L144 146" fill="none" stroke="url(#ll-accent)" stroke-width="1.3"/>
<g class="ll-key">
<circle class="ll-ring" pathLength="1" cx="115.6" cy="137.5" r="2.9" fill="none" stroke="url(#ll-accent)" stroke-width="1.9"/>
<path class="ll-shaft" pathLength="1" d="M119.25 137.5H128.6" fill="none" stroke="url(#ll-accent)" stroke-width="1.9"/>
<path class="ll-teeth" d="M127.65 137.5V140.6M125 137.5V140" fill="none" stroke="url(#ll-accent)" stroke-width="1.9"/>
</g>
<path class="ll-base" d="M30 152L40 147H202L212 152Z" fill="url(#ll-accent)"/>
<rect class="ll-spark" x="30" y="146.6" width="182" height="5.8" fill="url(#ll-spark)"/>
<g transform="translate(0 -4)">
<g filter="url(#ll-goo)">
<rect class="ll-pool" filter="url(#ll-blur-pool)" opacity="0" x="100" y="184.5" width="40" height="4.5" rx="2.25" fill="url(#ll-metal-text)"/>
<text class="ll-am-liquid" filter="url(#ll-blur-am)" opacity="0" x="120" y="188.5" text-anchor="middle" fill="url(#ll-metal-text)" style="${AM_STYLE}">ልዩ</text>
<text class="ll-en-liquid" filter="url(#ll-blur-en)" opacity="0" x="${LIYU_X}" y="188" text-anchor="middle" fill="url(#ll-metal-text)" style="${LIYU_STYLE}">LIYU</text>
</g>
<text class="ll-am" opacity="0" x="120" y="188.5" text-anchor="middle" fill="url(#ll-metal-text)" style="${AM_STYLE}">ልዩ</text>
<text class="ll-en" x="${LIYU_X}" y="188" text-anchor="middle" fill="url(#ll-metal-text)" style="${LIYU_STYLE}">LIYU</text>
<g mask="url(#ll-liyu)"><g transform="skewX(-16)"><rect class="ll-glint" x="80" y="160" width="16" height="34" fill="url(#ll-sheen)"/></g></g>
<path class="ll-rule ll-rule-l" fill="url(#ll-accent-text)"/>
<path class="ll-rule ll-rule-r" fill="url(#ll-accent-text)"/>
<text class="ll-mgmt" x="123.2" y="210" text-anchor="middle" fill="url(#ll-accent-text)" style="font:500 8.5px Montserrat,sans-serif;letter-spacing:6.4px">MANAGEMENT</text>
<text class="ll-motto" x="120.65" y="228" text-anchor="middle" fill="#B4BFCA" style="font:500 7.2px Montserrat,sans-serif;letter-spacing:1.3px">${mottoWords
  .map(
    ([w, blue], i) =>
      `<tspan class="ll-word"${blue ? ' fill="url(#ll-accent-text)"' : ""} style="--d:${(3.72 + i * 0.12).toFixed(2)}s">${w}</tspan>`,
  )
  .join(" ")}</text>
</g>
</g>
</svg>`;

/** When ልዩ forms, holds, and melts into LIYU, in seconds on the logo's own clock. */
const T = { inStart: 2.2, inEnd: 2.6, morphStart: 2.75, morphEnd: 3.4 };
const BLUR = 1.5;
const TINT = [0x9f / 255, 0xd0 / 255, 0xf7 / 255];
const ease = (x: number) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
const clamp = (x: number) => Math.max(0, Math.min(1, x));

/** Prepares one rendered logo and returns what drives its wordmark. */
function wire(svg: SVGSVGElement) {
  const q = <E extends Element>(sel: string) => {
    const el = svg.querySelector<E>(sel);
    if (!el) throw new Error(`Liyu logo is missing ${sel}`);
    return el;
  };
  const words = {
    am: {
      liquid: q<SVGTextElement>(".ll-am-liquid"),
      clean: q<SVGTextElement>(".ll-am"),
      blur: q(".ll-blur-am"),
    },
    en: {
      liquid: q<SVGTextElement>(".ll-en-liquid"),
      clean: q<SVGTextElement>(".ll-en"),
      blur: q(".ll-blur-en"),
    },
  };
  const pool = q(".ll-pool");
  const gooMatrix = q(".ll-goo-matrix");
  const amWidth = words.am.clean.getBBox().width || 40;
  const widthRatio = words.en.clean.getBBox().width / amWidth || 1;

  // MANAGEMENT tracks in letter by letter, so each letter becomes its own <text>
  // at the position the browser measured for it. Done once per logo.
  const mgmt = svg.querySelector<SVGTextElement>(".ll-mgmt");
  if (mgmt) splitLetters(mgmt);

  function splitLetters(mgmt: SVGTextElement) {
    const chars = [...(mgmt.textContent ?? "")];
    const xs = chars.map((_, i) => mgmt.getStartPositionOfChar(i).x);
    const mid = ((xs[0] ?? 0) + mgmt.getEndPositionOfChar(chars.length - 1).x) / 2;
    const letters = document.createElementNS("http://www.w3.org/2000/svg", "g");
    chars.forEach((c, i) => {
      const x = xs[i] ?? 0;
      const t = document.createElementNS("http://www.w3.org/2000/svg", "text");
      t.setAttribute("x", x.toFixed(2));
      t.setAttribute("y", mgmt.getAttribute("y") ?? "210");
      t.setAttribute("fill", mgmt.getAttribute("fill") ?? "");
      t.setAttribute(
        "style",
        (mgmt.getAttribute("style") ?? "").replace(/letter-spacing:[^;]+/, ""),
      );
      t.setAttribute("class", "ll-letter");
      t.style.setProperty("--dx", `${((x - mid) * 0.55).toFixed(1)}px`);
      t.textContent = c;
      letters.appendChild(t);
    });
    mgmt.replaceWith(letters);

    // Tapered blue rules either side of MANAGEMENT, echoing the base the towers stand on.
    const box = letters.getBBox();
    const y = box.y + box.height * 0.55;
    const l = box.x - 6;
    const r = box.x + box.width + 6;
    q(".ll-rule-l").setAttribute(
      "d",
      `M${l} ${y - 0.55}H${l - 19}L${l - 22} ${y}L${l - 19} ${y + 0.55}H${l}Z`,
    );
    q(".ll-rule-r").setAttribute(
      "d",
      `M${r} ${y - 0.55}H${r + 19}L${r + 22} ${y}L${r + 19} ${y + 0.55}H${r}Z`,
    );
  }

  // Scale around the middle of the baseline, so letters melt down onto it and rise back up from it.
  const stretch = (el: Element, sx: number, sy: number) =>
    el.setAttribute(
      "transform",
      `translate(120 188.5) scale(${sx.toFixed(4)} ${sy.toFixed(4)}) translate(-120 -188.5)`,
    );

  function showWord(w: (typeof words)["am"], f: number) {
    if (f <= 0.001) {
      w.liquid.setAttribute("opacity", "0");
      w.clean.setAttribute("opacity", "0");
      return;
    }
    const blur = Math.min(BLUR / f - BLUR, 30);
    const crisp = clamp((0.35 - blur) / 0.35);
    w.blur.setAttribute("stdDeviation", blur.toFixed(2));
    w.liquid.setAttribute("opacity", (Math.pow(f, 0.4) * (1 - crisp)).toFixed(3));
    w.clean.setAttribute("opacity", crisp.toFixed(3));
  }

  // Each word is blurred on its own and the pair is cut back to a hard edge, so as one word
  // blurs out and the other blurs in their shapes merge like liquid. Mid-morph the liquid
  // picks up the roof's sky blue by mixing each channel toward it inside the filter.
  function wordmarkAt(t: number) {
    let am = 0;
    let en = 0;
    let m = 0;
    if (t >= T.inStart && t < T.inEnd) am = ease((t - T.inStart) / (T.inEnd - T.inStart));
    else if (t >= T.inEnd && t < T.morphStart) am = 1;
    else if (t >= T.morphStart && t < T.morphEnd) {
      m = ease((t - T.morphStart) / (T.morphEnd - T.morphStart));
      am = 1 - m;
      en = m;
    } else if (t >= T.morphEnd) {
      en = 1;
      m = 1;
    }
    const melt = t >= T.morphStart && t < T.morphEnd ? Math.sin(Math.PI * m) : 0;
    const sAm = 1 + (widthRatio - 1) * m;
    const sy = 1 - 0.6 * melt;
    stretch(words.am.liquid, sAm, sy);
    stretch(words.am.clean, sAm, sy);
    stretch(words.en.liquid, sAm / widthRatio, sy);
    stretch(words.en.clean, sAm / widthRatio, sy);
    const poolWidth = amWidth * sAm * 0.92;
    pool.setAttribute("x", (120 - poolWidth / 2).toFixed(2));
    pool.setAttribute("width", poolWidth.toFixed(2));
    pool.setAttribute("opacity", Math.pow(melt, 0.7).toFixed(3));
    const row = (i: number) =>
      [0, 1, 2].map((j) => (j === i ? (1 - melt).toFixed(3) : 0)).join(" ") +
      ` 0 ${((TINT[i] ?? 0) * melt).toFixed(3)}`;
    gooMatrix.setAttribute("values", `${row(0)}  ${row(1)}  ${row(2)}  0 0 0 20 -6`);
    showWord(words.am, am);
    showWord(words.en, en);
  }

  return { wordmarkAt, clock: q(".ll-all") };
}

const STYLE_ID = "liyu-loader-style";
/** When the animation started, so the next page can carry on from the same moment. */
const STARTED_AT = "liyu.loaderStartedAt";
/** A start older than this belongs to some earlier visit, not to this sign-in. */
const CARRY_ON_LIMIT_MS = 20_000;
/** Past this the logo is finished; a light runs across it until the app is ready. */
const FINISHED_AT = 4.6;

const LOADER_CSS = `
.liyu-loader {
  position: fixed;
  inset: 0;
  z-index: 2147483000;
  display: grid;
  place-items: center;
  padding: 16px;
  background: rgba(15, 22, 30, 0.28);
  -webkit-backdrop-filter: blur(10px) saturate(1.1);
  backdrop-filter: blur(10px) saturate(1.1);
  opacity: 0;
  transition: opacity 0.2s ease;
}
.liyu-loader.is-open {
  opacity: 1;
}
.liyu-loader-card {
  width: min(320px, 78vw);
  padding: 24px 24px 18px;
  border-radius: 26px;
  background: rgba(29, 33, 39, 0.94);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 30px 80px -20px rgba(0, 0, 0, 0.55);
}
.liyu-loader-text {
  margin: 12px 0 0;
  text-align: center;
  font: 500 13px/1.4 "Noto Sans", "Noto Sans Ethiopic", system-ui, sans-serif;
  color: #aeb9c5;
}
.liyu-loader.is-waiting .liyu-loader-text {
  animation: ll-breathe 1.6s ease-in-out infinite;
}
.liyu-loader.is-waiting .ll-glint {
  animation: ll-glint 1.2s cubic-bezier(0.45, 0, 0.25, 1) 0.4s infinite;
}
@keyframes ll-breathe {
  50% {
    opacity: 0.55;
  }
}
@media (prefers-reduced-motion: reduce) {
  .liyu-loader {
    transition: none;
  }
  .liyu-loader.is-waiting .liyu-loader-text,
  .liyu-loader.is-waiting .ll-glint {
    animation: none;
  }
}
.liyu-logo {
  display: block;
  width: 100%;
  height: auto;
  overflow: visible;
}
.liyu-logo * {
  transform-box: fill-box;
}
/* The morph scales the words around fixed points in the logo, not around their own boxes. */
.liyu-logo .ll-am-liquid,
.liyu-logo .ll-en-liquid,
.liyu-logo .ll-am,
.liyu-logo .ll-en {
  transform-box: view-box;
}
.liyu-loader .liyu-logo:not(.is-playing):not(.is-done) {
  visibility: hidden;
}
.ll-glow,
.ll-tip,
.ll-spark,
.ll-lit {
  opacity: 0;
}
.ll-sheen {
  transform: translateX(-45px);
}
.ll-glint {
  transform: translateX(-40px);
}
.ll-teeth {
  transform-origin: 50% 0;
}
.ll-key {
  transform-origin: 50% 40%;
}
.ll-rule-l {
  transform-origin: 100% 50%;
}
.ll-rule-r {
  transform-origin: 0% 50%;
}
.is-playing .ll-all {
  animation: ll-settle 4.2s cubic-bezier(0.2, 0.7, 0.2, 1) both;
  transform-origin: 50% 50%;
}
.is-playing .ll-base {
  animation: ll-grow-x 0.4s cubic-bezier(0.2, 0.8, 0.2, 1) both;
  transform-origin: 50% 50%;
}
.is-playing .ll-spark {
  animation: ll-spark 0.4s cubic-bezier(0.2, 0.8, 0.2, 1) both;
  transform-origin: 50% 50%;
}
.is-playing .ll-floor {
  animation: ll-rise 0.38s cubic-bezier(0.2, 0.7, 0.2, 1) var(--d) both;
}
.is-playing .ll-lit {
  animation: ll-lit 0.55s ease-out calc(var(--d) + 0.1s) both;
}
.is-playing .ll-roofclip {
  animation: ll-grow-x 0.45s cubic-bezier(0.3, 0.6, 0.2, 1) 0.75s both;
  transform-origin: 50% 50%;
}
.is-playing .ll-spire {
  animation: ll-grow-y 0.3s cubic-bezier(0.3, 0.8, 0.2, 1.2) 0.95s both;
  transform-origin: 50% 100%;
}
.is-playing .ll-tip {
  animation: ll-flare 0.55s ease-out 1.2s both;
}
.is-playing .ll-inner {
  animation: ll-fade 0.35s ease 1.1s both;
}
/* The dash only exists while drawing (fill "backwards"), so the finished ring and shaft are solid. */
.is-playing .ll-ring {
  animation: ll-draw 0.35s cubic-bezier(0.4, 0, 0.2, 1) 1.15s backwards;
}
.is-playing .ll-shaft {
  animation: ll-draw 0.25s cubic-bezier(0.3, 0.7, 0.2, 1) 1.45s backwards;
}
.is-playing .ll-teeth {
  animation: ll-drop 0.22s cubic-bezier(0.2, 0.8, 0.2, 1) 1.65s both;
}
.is-playing .ll-key {
  animation: ll-turn 0.35s cubic-bezier(0.5, 0, 0.3, 1) 1.9s both;
}
.is-playing .ll-glow {
  animation: ll-glow 1.1s ease-out 2.1s both;
}
.is-playing .ll-sheen {
  animation: ll-sweep 0.85s cubic-bezier(0.45, 0, 0.25, 1) 2.1s both;
}
.is-playing .ll-letter {
  animation: ll-track 0.7s cubic-bezier(0.2, 0.8, 0.2, 1) 3.3s both;
}
.is-playing .ll-rule {
  animation: ll-grow-x 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) 3.62s both;
}
.is-playing .ll-glint {
  animation: ll-glint 0.55s cubic-bezier(0.45, 0, 0.25, 1) 3.6s both;
}
.is-playing .ll-motto {
  animation: ll-settle-up 0.7s cubic-bezier(0.2, 0.8, 0.2, 1) 3.7s both;
}
.is-playing .ll-word {
  animation: ll-word 0.45s ease-out var(--d) both;
}
@keyframes ll-settle {
  from {
    transform: scale(1.045);
  }
}
@keyframes ll-grow-x {
  from {
    transform: scaleX(0);
  }
}
@keyframes ll-grow-y {
  from {
    transform: scaleY(0);
  }
}
@keyframes ll-spark {
  0% {
    opacity: 1;
    transform: scaleX(0);
  }
  70% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: scaleX(1);
  }
}
@keyframes ll-rise {
  from {
    opacity: 0;
    transform: translateY(3px);
  }
}
@keyframes ll-lit {
  0% {
    opacity: 0;
  }
  25% {
    opacity: 0.9;
  }
  100% {
    opacity: 0;
  }
}
@keyframes ll-flare {
  0% {
    opacity: 0;
    transform: scale(0.3);
  }
  30% {
    opacity: 1;
    transform: scale(1.15);
  }
  100% {
    opacity: 0;
    transform: scale(0.8);
  }
}
@keyframes ll-fade {
  from {
    opacity: 0;
  }
}
@keyframes ll-drop {
  from {
    opacity: 0;
    transform: scaleY(0);
  }
}
@keyframes ll-draw {
  0% {
    opacity: 0;
    stroke-dasharray: 1 2;
    stroke-dashoffset: 1;
  }
  2% {
    opacity: 1;
  }
  100% {
    stroke-dasharray: 1 2;
    stroke-dashoffset: 0;
  }
}
@keyframes ll-turn {
  45% {
    transform: scaleY(-0.15);
  }
}
@keyframes ll-glow {
  0% {
    opacity: 0;
  }
  30% {
    opacity: 0.55;
  }
  100% {
    opacity: 0;
  }
}
@keyframes ll-sweep {
  from {
    transform: translateX(-45px);
  }
  to {
    transform: translateX(215px);
  }
}
@keyframes ll-track {
  from {
    opacity: 0;
    transform: translateX(var(--dx));
  }
}
@keyframes ll-glint {
  from {
    transform: translateX(-40px);
  }
  to {
    transform: translateX(110px);
  }
}
@keyframes ll-settle-up {
  from {
    transform: translateY(4px);
  }
}
@keyframes ll-word {
  from {
    fill-opacity: 0;
  }
}
`;

export interface Loader {
  /** Fades the loader out and forgets its start time. Safe to call more than once. */
  hide(): void;
}

function injectStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = LOADER_CSS;
  document.head.appendChild(style);
}

/** Seconds since a loader on the previous page started, or null if there was none. */
function carriedOver(): number | null {
  try {
    const started = Number(sessionStorage.getItem(STARTED_AT));
    const age = Date.now() - started;
    return started > 0 && age >= 0 && age < CARRY_ON_LIMIT_MS ? age / 1000 : null;
  } catch {
    // Storage can be switched off; the animation then simply starts from the top.
    return null;
  }
}

function remember(startedAt: number | null): void {
  try {
    if (startedAt === null) sessionStorage.removeItem(STARTED_AT);
    else sessionStorage.setItem(STARTED_AT, String(startedAt));
  } catch {
    // Without storage the next page starts its own animation, which is fine.
  }
}

/**
 * Shows the loader now. With `carryOn`, it picks the animation up at the moment
 * a loader on the previous page had reached.
 */
export function showLoader(message: string, { carryOn = false } = {}): Loader {
  injectStyle();
  const offset = carryOn ? (carriedOver() ?? 0) : 0;
  remember(Date.now() - offset * 1000);

  const root = document.createElement("div");
  root.className = "liyu-loader";
  root.setAttribute("role", "status");
  root.setAttribute("aria-live", "polite");
  const card = document.createElement("div");
  card.className = "liyu-loader-card";
  card.innerHTML = LOGO_SVG;
  const text = document.createElement("p");
  text.className = "liyu-loader-text";
  text.textContent = message;
  card.appendChild(text);
  root.appendChild(card);
  document.body.appendChild(root);
  // The card appears together with its logo, never empty. Carrying on from the form it is
  // already on screen, so it does not fade in again.
  const open = () => {
    if (offset > 0) root.classList.add("is-open");
    else requestAnimationFrame(() => root.classList.add("is-open"));
  };

  const svg = card.querySelector<SVGSVGElement>("svg.liyu-logo");
  let raf = 0;
  let hidden = false;

  function start() {
    if (hidden || !svg) return;
    const logo = wire(svg);
    open();
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) {
      logo.wordmarkAt(99);
      svg.classList.add("is-done");
      return;
    }
    svg.classList.add("is-playing");
    if (offset > 0) {
      for (const animation of svg.getAnimations({ subtree: true })) {
        animation.currentTime = offset * 1000;
      }
    }
    // The wordmark reads its time from the logo's own CSS clock, so it can never run ahead
    // of the building: if the browser holds the CSS back, ልዩ waits too.
    const clock = logo.clock.getAnimations()[0];
    const step = () => {
      const t = clock ? (Number(clock.currentTime) || 0) / 1000 : FINISHED_AT;
      logo.wordmarkAt(t);
      if (t < FINISHED_AT) raf = requestAnimationFrame(step);
      else root.classList.add("is-waiting");
    };
    raf = requestAnimationFrame(step);
  }

  // Wait briefly for the logo's fonts so letters are measured where they are drawn, but never
  // hold the loader back for them. Carrying on, the previous page has already loaded them.
  void Promise.race([
    Promise.all([
      document.fonts.load("600 24px Montserrat", "LIYU"),
      document.fonts.load("500 8.5px Montserrat", "MANAGEMENT"),
      document.fonts.load("600 27px 'Noto Sans Ethiopic'", "ልዩ"),
    ]),
    new Promise((resolve) => setTimeout(resolve, offset > 0 ? 120 : 400)),
  ])
    .catch((cause: unknown) => {
      console.error("The loader's fonts did not load; drawing with fallbacks", cause);
    })
    .then(start)
    .catch((cause: unknown) => {
      // A logo that cannot animate is still a loading screen; show it finished.
      svg?.classList.add("is-done");
      open();
      console.error("The loader's logo could not animate", cause);
    });

  return {
    hide() {
      if (hidden) return;
      hidden = true;
      cancelAnimationFrame(raf);
      remember(null);
      root.classList.remove("is-open");
      setTimeout(() => root.remove(), 220);
    },
  };
}

/**
 * For a wait that is often quick: shows the loader only if it is still waiting
 * after `delayMs`, so a fast answer never flashes it. Carries on straight away,
 * with no delay, when a loader on the previous page was still showing.
 */
export function showLoaderIfSlow(message: string, delayMs = 300): Loader {
  if (carriedOver() !== null) return showLoader(message, { carryOn: true });
  let shown: Loader | null = null;
  let cancelled = false;
  const timer = setTimeout(() => {
    if (!cancelled) shown = showLoader(message);
  }, delayMs);
  return {
    hide() {
      cancelled = true;
      clearTimeout(timer);
      shown?.hide();
    },
  };
}
